import React, { useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";

import { CaptureProvider } from "../../../contexts/capture";
import {
  buildAssetPatch,
  fetchAssetValues,
  prepareAssetInputWithCustomFields,
} from "../../../utils/asset";
import { AssetsProvider } from "../../../contexts/assets";
import CollectionProvider from "../../../views/capture/CollectionProvider";
import {
  ASSET_QUERY,
  ASSETS_BULK_REMOVE_MUTATION,
  UPDATE_ASSET_MUTATION,
} from "../../../constants/graphql";

import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import ConfirmDeleteModal from "../../../components/images/ConfirmDeleteModal";
import FormPage from "../../capture/ReceivingWizard/Items/ItemDetails/FormPage";

// TODO: remove this
const shipment = {};

export default function ItemDetails({ id, project, shipmentItem, refetch, onClose, sameLocation }) {
  const [openRemoveItem, setOpenRemoveItem] = useState(false);
  const [openSavePrompt, setOpenSavePrompt] = useState(false);
  const pendingSaveRef = useRef(null);
  const isDraft = false;
  const { data: { asset = {} } = {}, loading } = useQuery(ASSET_QUERY, {
    variables: { id },
    fetchPolicy: "network-only",
  });
  const [updateAsset, { loading: savingAsset }] = useMutation(UPDATE_ASSET_MUTATION);
  const [removeAsset, { loading: removing }] = useMutation(ASSETS_BULK_REMOVE_MUTATION);
  const { notifySuccess } = useNotification();

  const initialValues = useMemo(
    () => ({
      ...fetchAssetValues(asset),
      availableQuantity: asset.availableQuantity,
    }),
    [asset]
  );

  const normalizePatch = patch => {
    const nextPatch = { ...patch };

    if (nextPatch.quantityOrdered !== undefined && nextPatch.quantityOrdered !== "") {
      nextPatch.quantityOrdered = parseInt(nextPatch.quantityOrdered, 10);
    }

    if (nextPatch.quantityReceived !== undefined && nextPatch.quantityReceived !== "") {
      nextPatch.quantityReceived = parseInt(nextPatch.quantityReceived, 10);
    }

    return nextPatch;
  };

  const persistSave = async ({ patch, propagateToMatchingAssets = false }) => {
    const input = prepareAssetInputWithCustomFields(normalizePatch(patch), asset);
    const response = await updateAsset({
      variables: {
        id,
        input,
        propagateToMatchingAssets,
        previousSkuNumber: asset.skuNumber,
        previousDescription: asset.description,
      },
    });
    const propagatedAssetsCount = response?.data?.updateAsset?.propagatedAssetsCount || 0;

    if (refetch) {
      await refetch();
    }

    if (propagateToMatchingAssets) {
      notifySuccess(
        propagatedAssetsCount > 0
          ? `SKU updated and synced to ${propagatedAssetsCount} matching item${propagatedAssetsCount === 1 ? "" : "s"}`
          : "SKU updated. No matching items needed syncing."
      );
    } else {
      notifySuccess("Asset was updated");
    }

    onClose();

    return response;
  };

  const clearPendingSave = () => {
    pendingSaveRef.current = null;
  };

  const closeSavePrompt = () => {
    setOpenSavePrompt(false);
    pendingSaveRef.current?.resolve?.(null);
    clearPendingSave();
  };

  const submitPendingSave = async propagateToMatchingAssets => {
    const pendingSave = pendingSaveRef.current;
    if (!pendingSave) return;

    setOpenSavePrompt(false);

    try {
      const response = await persistSave({
        patch: pendingSave.patch,
        propagateToMatchingAssets,
      });
      pendingSave.resolve?.(response);
    } catch (error) {
      pendingSave.reject?.(error);
    } finally {
      clearPendingSave();
    }
  };

  const handleSave = async ({
    attachments,
    availableQuantity,
    shipmentItemQuantity,
    expectedQuantity,
    palletsReceivedCount,
    itemNotes,
    totalPieces,
    ...values
  }) => {
    const patch = buildAssetPatch(values, initialValues);
    if (Object.keys(patch).length === 0) {
      onClose();
      return null;
    }

    if (asset.type !== "Sku") {
      return persistSave({ patch });
    }

    return new Promise((resolve, reject) => {
      pendingSaveRef.current = { patch, resolve, reject };
      setOpenSavePrompt(true);
    });
  };

  const handleRemoveAsset = async () => {
    await removeAsset({ variables: { ids: [asset.id] } });
    await refetch();
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const toggleRemoveItem = () => setOpenRemoveItem(prev => !prev);

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={handleClose}>
      <DialogTitle sx={{ background: "#f4f6f8", display: "flex", justifyContent: "space-between" }}>
        <Box width="48px" />
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Item Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ background: "#f4f6f8" }}>
        {loading ? (
          <Box height={258} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box>
            <CaptureProvider>
              <CollectionProvider skipQuery>
                <AssetsProvider>
                  <Formik key={asset?.id} initialValues={initialValues} onSubmit={handleSave}>
                    <FormPage
                      type="item"
                      shipmentId={shipment.id}
                      currentAsset={{ ...asset }}
                      onCloseModal={onClose}
                      handeRefetch={refetch}
                      shipmentItem={shipmentItem}
                      removing={removing}
                      toggleRemoveItem={toggleRemoveItem}
                      isDraft={isDraft}
                      onMarkAsReceived={() => {}}
                      sameLocation={sameLocation}
                      project={project}
                      displayType="fieldSettings"
                      overrideDisplayFields={{
                        available_quantity: false,
                      }}
                    />
                  </Formik>
                </AssetsProvider>
              </CollectionProvider>
            </CaptureProvider>
          </Box>
        )}
      </DialogContent>
      {openRemoveItem && (
        <ConfirmDeleteModal
          title="Delete Item"
          descr="Are you sure want to delete the selected item?"
          onClose={toggleRemoveItem}
          onDelete={handleRemoveAsset}
        />
      )}
      <Dialog open={openSavePrompt} onClose={closeSavePrompt} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            color: "text.primary",
            fontSize: 24,
            fontWeight: 500,
            textAlign: "center",
            position: "relative",
            pt: 3,
          }}
        >
          Apply SKU updates?
          <IconButton
            aria-label="Close"
            onClick={closeSavePrompt}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Would you like to save these changes only to this SKU, or also push them to matching
            asset records in this project that share the same SKU number and description?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => submitPendingSave(false)} disabled={savingAsset}>
            Save Only
          </Button>
          <Button variant="contained" onClick={() => submitPendingSave(true)} disabled={savingAsset}>
            Save and Update Matching Items
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
